import React, {useEffect, useState} from 'react'
import {MemberGroup} from 'src/ClientManagement/models/InvestorGroupType'
import {RedAsterisk} from 'src/components'
import {selectReleaseToggles} from 'src/ReleaseToggles/releaseTogglesSlice'
import {useAppSelector} from 'src/store/hooks'
import {FamilyMembers, Member, Trustee} from '../../../model/EstateFlowchartTypes'
import {familyMembersSelector} from '../../../slices/EstateFlowChartSlice'
import {parseFamilyMembers} from "../../../utils/DefUtils"
import DropdownMultiselect from '../DropdownMultiselect/DropdownMultiselect'

const addNT = (members: Member[], selectedOptions: Trustee[], includeNT: boolean) => {
    const NT: Member = { value: "", label: "Northern Trust", selected: true };
    if (includeNT) {
        const hasNorthernTrust = selectedOptions?.some(member => member.customName === "Northern Trust") || members.some(member => member.label === "Northern Trust");
        if (!hasNorthernTrust) {
            members.push(NT);
        }
    }
}

const addPrimaryMember = (members: Member[], includePrimaryMember: boolean, primaryMember: Member) => {
    if (includePrimaryMember) {
        const hasPrimaryMember = members.some(member => member.label === primaryMember.label);
        if (!hasPrimaryMember) {
            members.push(primaryMember);
        }
    }
}

type MemberDropdownMultiselectProps = {
    className?: string,
    id?: string,
    label?: string,
    required: boolean,
    options: Member[] | MemberGroup,
    selectedOptions?: Trustee[] | string[],
    includeNT?: boolean,
    includePrimaryMember?: boolean,
    panelHeight?: number,
    onChange: Function,
    onError?: string,
    useTags?: boolean,
    helperText?: string,
    placeholder?: string,
}

/**
 * MemberDropdownMultiselect component renders a dropdown multiselect input for selecting members such as trustees or grantors.
 * It acts as a wrapper for the DDS DropdownMultiselect component, providing additional functionality and customization options.
 *
 * @param {Object} props - The properties object.
 * @param {string} [props.className] - Optional CSS class name for the component.
 * @param {string} [props.id] - Optional ID for the component.
 * @param {string} [props.label] - The label for the dropdown.
 * @param {boolean} props.required - Indicates if the dropdown is required.
 * @param {Array<Member>|MemberGroup} props.options - The list of options available for selection, either as an array of members or a member group.
 * @param {Array<Trustee|string>} [props.selectedOptions] - The list of currently selected options, either as trustees or strings.
 * @param {boolean} [props.includeNT=false] - Flag to include "Northern Trust" as an option.
 * @param {boolean} [props.includePrimaryMember=true] - Flag to include the primary member as an option.
 * @param {number} [props.panelHeight] - Optional height for the dropdown panel.
 * @param {Function} props.onChange - Callback function to handle changes in selection.
 * @param {string} [props.onError] - Error message to display if there is an issue.
 * @param {boolean} [props.useTags=false] - Flag to use tags for selected items or render a comma-separated list.
 * @param {string} [props.helperText="Add a new name, if it is not in the list."] - Helper text to display below the dropdown.
 * @param {string} [props.placeholder=""] - Placeholder text to display when no options are selected.
 *
 * @returns {JSX.Element} The rendered MemberDropdownMultiselect component.
 */
function MemberDropdownMultiselect({
    label,
    required,
    options,
    selectedOptions,
    includeNT = false,
    includePrimaryMember = true,
    panelHeight,
    onChange,
    onError,
    useTags = false,
    helperText,
    placeholder = "",
}
    : Readonly<MemberDropdownMultiselectProps>): JSX.Element {
    const [members, setMembers] = useState<Member[]>([]);
    const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
    const [showTags, setShowTags] = useState<boolean>(useTags);
    const familyMembers = useAppSelector(familyMembersSelector);
    const releaseToggles = useAppSelector(selectReleaseToggles);

    const createTags = () => {
        return selectedMembers.map((element, index) => {
            const member = members.find(member => member.value === element)
            const ddsTagTextClass = member?.label.indexOf(" ") === -1 ? "dds-tag-text-truncate" : "dds-tag-text"
            return member && (
                <span key={`member-tag-${member.value}`} className="dds-tag" title={`${member.label}`} data-testid={`${member.label}-member-tag`}>
                    <span key={`member-tag-text-${member.value}`} className={ddsTagTextClass}>{member.label}</span>
                    <button translate="no"
                        className="dds-icons icon"
                        aria-hidden="false"
                        data-testid={`remove-member-${member.label}`}
                        onClick={removeSelectedMember}>
                        clear
                    </button>
                </span>
            )
        })
    }

    const reBuildOptions = () => {
        if (Array.isArray(options)) {
            let newMembers = [...members, ...options];
            addNT(newMembers, selectedOptions as Trustee[], includeNT);
            addPrimaryMember(newMembers, includePrimaryMember, {value: familyMembers.primaryMember.id, label: familyMembers.primaryMember.firstName + " " + familyMembers.primaryMember.lastName, selected: false});
            newMembers = newMembers.filter(
                (member, index, self) =>
                    index === self.findIndex(m => m.value === member.value)
            );
            setMembers(newMembers);
            return;
        }
        const parsedFamilyMembers: FamilyMembers = parseFamilyMembers(options);
        let newMembers: Member[] = [...members, ...parsedFamilyMembers.familyMember]; // members contains all options (new and prexisting)

        const customMembers: Member[] = (selectedOptions as Trustee[] || [])
            .filter(member => !member.familyMember && (!releaseToggles?.enableEstateFlowchartMVPRelease || (member.memberId && member.memberId != "")))
            .map(member => ({
                label: member.customName ?? "",
                selected: true,
                value: member.memberId ?? ""
            }))

        newMembers.push(...customMembers)

        if (includePrimaryMember) {
            newMembers.push({
                selected: false,
                value: parsedFamilyMembers.value,
                label: parsedFamilyMembers.label
            });
        }

        addNT(newMembers, selectedOptions as Trustee[], includeNT);
        newMembers = newMembers.filter(
            (member, index, self) =>
                index === self.findIndex(m => m.value === member.value)
        );
        const ntWithEmptyValueIndex = newMembers.findIndex(
            member => member.label === "Northern Trust" && member.value === ""
        );
        const ntWithNonEmptyValueExists = newMembers.some(
            member => member.label === "Northern Trust" && member.value !== ""
        );
        if (ntWithEmptyValueIndex !== -1 && ntWithNonEmptyValueExists) {
            newMembers.splice(ntWithEmptyValueIndex, 1);
        }
        setMembers(Array.from(new Set(newMembers))); // remove duplicates
    }

    useEffect(() => {
        reBuildOptions();
        if (selectedOptions && typeof selectedOptions[0] !== 'string') { 
            const membersSelected: string[] | undefined = (selectedOptions as Trustee[])?.map(member => member.memberId ?? member.customName ?? "");
            const membersSelectedWithDashes = membersSelected?.map(member => typeof member === 'string' ? member.replace(/\s+/g, '-') : member); // options.value has - instead of space. we are matching here.
            setSelectedMembers(Array.from(new Set(membersSelectedWithDashes)) ?? []); // populate selected members
        }else{
            setSelectedMembers(Array.from(new Set(selectedOptions as string[])))
        }
    }, [options, selectedOptions])

    const handleBlur = (e: any) => {
        const relatedTarget = e.relatedTarget;
        const isButtonOrSubmit = relatedTarget?.type === "button" || relatedTarget?.type === "submit";

        if (!e.currentTarget.contains(relatedTarget) || isButtonOrSubmit) {
            if (isButtonOrSubmit && relatedTarget?.tabIndex === 0) {
                const observer = new MutationObserver((mutations, obs) => {
                    const inputWrapper = document.getElementById(`${label?.toLowerCase()}-DDMS-creatable`);
                    const inputElement = inputWrapper?.querySelector('input');
                    if (inputElement) {
                        inputElement.focus();
                        obs.disconnect(); // Stop observing once the input is found and focused
                        swap();
                    }
                });

                observer.observe(document.body, { childList: true, subtree: true });
            } else {
                swap();
            }
        }
    }

    const swap = () => {
        if (useTags) {
            setShowTags(!showTags);
            const observer = new MutationObserver(() => {
                const inputWrapper = document.getElementById(`${label?.toLowerCase()}-DDMS-creatable`);
                const inputElement = inputWrapper?.querySelector('input');
                if (inputElement) {
                    inputElement.focus();
                    observer.disconnect(); // Stop observing once the input is found and focused
                }
            });

            observer.observe(document.body, { childList: true, subtree: true });
        }
    }

    const removeSelectedMember = (e: any) => {
        e.stopPropagation()
        const member = e.currentTarget.parentElement?.textContent;
        const memberName = member?.split('clear')[0].trim();
        const updatedSelectedMembers = selectedMembers.filter(memberId => {
            const member = members.find(m => m.value === memberId);
            return member?.label !== memberName;
        });
        setSelectedMembers(updatedSelectedMembers);
        let memberOptions: Member[] = members.filter((m: Member) => updatedSelectedMembers.includes(m.value))
            .map(m => ({
                ...m,
                selected: true
            }));
        memberOptions = updatedSelectedMembers.map(memberId =>
            members.find(m => m.value === memberId)
        ).filter(Boolean) as Member[];
        onChange(memberOptions);
    }

    const clearAll = (e: any) => {
        e.stopPropagation();
        setSelectedMembers([]);
        let memberOptions: Member[] = [];
        onChange(memberOptions);
    }

    const labelElement = label ? <label className={"h5"}>{label}{required ? <RedAsterisk /> : null}</label> : null

    if (useTags && showTags) {
        return (
            <div className="layout-data-entry-form__field" data-testid={`${label?.toLowerCase()}-dropdown`}>
                {labelElement}
                <div className="custom-dds-container"
                    role="button"
                    tabIndex={-1}
                    data-testid={`${label?.toLowerCase() ?? "successor"}-custom-dds-container`}
                    id={`${label?.toLowerCase()}-custom-dds-container`}
                    onClick={swap}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') swap()
                    }
                    }>
                    <div className={onError ? "error custom-dds" : "custom-dds"}>
                        {createTags()}
                        {selectedMembers.length === 0 && placeholder &&
                            <span className="placeholder">{placeholder}</span>}
                        {selectedMembers.length > 1 &&
                            <i translate="no" className={"dds-icons icon custom-clear"} data-testid="clear-all-button" aria-hidden="true"
                                onClick={clearAll}>clear</i>}
                        <i translate="no"
                            className={onError ? "dds-icons icon custom-dropdown input__icon-error" : "dds-icons icon custom-dropdown"}
                            aria-hidden="true">arrow_drop_down</i>
                    </div>
                    {helperText && !onError && <span className="input__info margintop-sm">{helperText}</span>}
                    {onError ? <span className="input__error input__info margintop-sm">{onError}</span> : null}
                </div>
            </div>
        )
    } else {
        return (
            <div className="layout-data-entry-form__field" data-testid={`${label?.toLowerCase()}-dropdown`}
                onBlur={handleBlur}
                role="button"
                tabIndex={-1}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'Escape') swap()
                }}>
                {labelElement}
                <div className="width-100p textalign-left"
                    id={`${label?.toLowerCase()}-DDMS-creatable`}
                    data-testid={`${label?.toLowerCase()}-DDMS-creatable`}>
                    <DropdownMultiselect
                        id={label?.toLowerCase()}
                        className={'dds-ms'}
                        creatable
                        error={onError ?? undefined}
                        hideSelectAll
                        label=""
                        onChange={(memberOptions: any) => {
                            setSelectedMembers(memberOptions.map((member: Member) => member.value));
                            onChange(memberOptions)
                        }}
                        open={useTags}
                        options={members}
                        panelHeight={panelHeight ?? members.length * 34 < 280 ? (members.length * 34) + 20 : 280}
                        placeholder={placeholder}
                        required={false}
                        searchable
                        selectAllLabel=""
                        selected={selectedMembers}
                        selectedDisplayTextAfter=""
                        selectedDisplayTextBefore=""
                        showTotalSelected={false}
                        showClearAllButton={selectedMembers.length > 1}
                        showFullSelected={true}
                        helperText={helperText}
                    />
                </div>
            </div>
        )
    }
}

export default MemberDropdownMultiselect